import React from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import {
  colors,
  Flex,
  Heading,
  Text,
  isElectricVehicle,
  Box,
  BoxProps,
} from '@weareberyl/design-system'

import { ModuleLink, VehicleLink } from '~/components/Link'
import FrameTypeIcon from './FrameTypeIcon'
import * as routes from '~/constants/routes'
import batteryIcon from '~/images/battery.svg'

import { PermissionOnly } from '~/components/Session'
import Dropdown from '~/components/shared/Dropdown'
import { MenuOutlined } from '@ant-design/icons'
import { VehicleContextMenu } from '~/components/Module/context'
import {
  FrameType,
  LockConfiguration,
  ModuleHardware,
  type ModuleSummaryQuery_module_Module_tags_ModuleTag,
  type Scheme,
} from '~/gql/generated/graphql'
import Tags from '~/components/Module/Tags'
import { isNotNullOrUndefined } from '~/utils/types'

interface IBatteryPercentage {
  batteryPercent: number
  size?: 'large' | 'medium'
}

const BatteryPercentage = ({ size, batteryPercent }: IBatteryPercentage) => {
  return (
    <Flex mt={size === 'large' ? 1 : 0} ml={2}>
      <img
        alt="electric"
        style={{ height: 18, marginTop: 2 }}
        src={batteryIcon}
      />
      <Text ml={1}>{batteryPercent || '- '}%</Text>
    </Flex>
  )
}

export const getVehicleName = (frameType: FrameType | undefined) => {
  switch (frameType) {
    case FrameType.beryl_bike:
    case FrameType.pashley_westmidlands_bike:
      return 'Bike'
    case FrameType.beryl_e_bike:
    case FrameType.beryl_e_bike_2:
    case FrameType.pashley_westmidlands_e_bike:
    case FrameType.segway_e_bike:
    case FrameType.segway_e_bike_b200:
      return 'e-Bike'
    case FrameType.pashley_tfl:
      return 'SmartBike'
    case FrameType.okai_scooter:
      return 'e-Scooter'
    case FrameType.raleigh_cargo_e_bike:
      return 'e-Cargo Bike'
    default:
      return 'Vehicle'
  }
}

export const getVehicleTitle = (
  frameId?: string | null,
  frameType?: FrameType | null,
  moduleId?: string | null,
) => {
  if (frameId) {
    return `${frameType ? getVehicleName(frameType) : 'Vehicle'} ${frameId}`
  }

  if (moduleId) {
    return `Module ${moduleId}`
  }

  return 'Unknown Frame ID'
}

type ModuleHeaderProps = RouteComponentProps & {
  moduleId: string | null
  frameId?: string | null
  scheme?: Pick<Scheme, 'id'> | null
  frameType: FrameType | null | undefined
  hardwareType: ModuleHardware | null
  vehicleId?: string
  batteryPercent: number | null | undefined
  size?: 'large' | 'medium'
  location: any
  lockConfiguration?: LockConfiguration
  tags?: ModuleSummaryQuery_module_Module_tags_ModuleTag[] | null
  scooterSpeedLimited?: boolean | null
}

const ModuleHeader = withRouter(
  ({
    moduleId,
    frameId,
    scheme,
    frameType,
    hardwareType,
    vehicleId,
    batteryPercent,
    size = 'large',
    location,
    lockConfiguration,
    tags,
    scooterSpeedLimited,
  }: ModuleHeaderProps) => {
    const URLS = [
      `${routes.MODULES}/${moduleId}`,
      `${routes.VEHICLES}/by_module_id/${moduleId}`,
      `/bike/${moduleId}`,
    ]
    const summaryTitle = getVehicleTitle(frameId, frameType, moduleId)

    const Title = () => {
      const TitleText = (props: BoxProps) => (
        <Heading variant={size === 'large' ? 'h1' : 'h3'} {...props}>
          {summaryTitle}
        </Heading>
      )

      if (URLS.some(url => location.pathname.includes(url)) || !moduleId)
        return <TitleText />

      if (scheme)
        return (
          <VehicleLink id={moduleId} bikeScheme={scheme}>
            <TitleText color={colors.jade} />
          </VehicleLink>
        )

      return (
        <ModuleLink id={moduleId}>
          <TitleText color={colors.jade} />
        </ModuleLink>
      )
    }

    return (
      <Flex width="100%">
        <Flex flexDirection="row" width="100%">
          <Flex flex={4}>
            <FrameTypeIcon
              frameType={frameType}
              mt={size === 'large' ? 2 : '5px'}
            />
            <Title />
            {isNotNullOrUndefined(batteryPercent) && batteryPercent > 0 && (
              <BatteryPercentage batteryPercent={batteryPercent} size={size} />
            )}
          </Flex>
          <Flex flexDirection="row" justifyContent="end" flex={1}>
            {moduleId && hardwareType && vehicleId && (
              <PermissionOnly permission="module.actions">
                <Dropdown
                  overlay={
                    <VehicleContextMenu
                      module_id={moduleId}
                      hardware_type={hardwareType}
                      vehicleId={vehicleId}
                      lock_configuration={lockConfiguration}
                    />
                  }
                >
                  <MenuOutlined
                    data-testid="actions-menu"
                    style={{ cursor: 'pointer' }}
                  />
                </Dropdown>
              </PermissionOnly>
            )}
          </Flex>
        </Flex>
        {tags && (
          <Box pt={2}>
            <Tags tags={tags} vehicleId={vehicleId} />
          </Box>
        )}
        {scooterSpeedLimited && (
          <Box pt={2}>
            <Text ml={1} fontFamily="Hellix-SemiBold" variant="small">
              ⚠️ Speed has been limited
            </Text>
          </Box>
        )}
      </Flex>
    )
  },
)

export default ModuleHeader
